import { useEffect, useState } from 'react';
import { Gauge, Crown, UserRound, Infinity as InfiniteIcon } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { getLimitsOverview } from '../../services/paymentService';
import { getModulesList } from '../../services/modulesService';
import { nomLocalise, limitesDuModule, cleFenetre } from '../../utils/limitesUsage';

const lire = (o, nom) => o?.[nom] ?? o?.[nom.toLowerCase()] ?? o?.[nom.toUpperCase()];

function remplir(gabarit, valeurs) {
    if (!gabarit) return '';
    return Object.keys(valeurs).reduce((texte, clef) => texte.replace(`{${clef}}`, valeurs[clef]), gabarit);
}

/** Profil + abonnements : le même en-tête sur la page Limites et dans chaque module. */
export function EnTeteAbonnement({ vue, language, libelles }) {
    const profil = nomLocalise(lire(vue, 'profileType'), language);
    const abonnements = lire(vue, 'subscriptions') || [];
    const noms = abonnements.map((a) => nomLocalise(a, language)).filter(Boolean);

    return (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-testid="limites-entete">
            <div className="flex items-center gap-2">
                <UserRound className="w-4 h-4 text-gray-400 shrink-0" />
                <dt className="text-gray-500">{libelles.profileType}</dt>
                <dd className="font-medium text-gray-900">{profil || '—'}</dd>
            </div>
            <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-gray-400 shrink-0" />
                <dt className="text-gray-500">{libelles.subscription}</dt>
                <dd className="font-medium text-gray-900" data-testid="limites-abonnement">
                    {noms.length > 0 ? noms.join(', ') : libelles.planFree}
                </dd>
            </div>
        </dl>
    );
}

/** Une limite : libellé, jauge et fenêtre éventuelle. */
export function LigneLimite({ limite, libelles, windows }) {
    const nom = libelles.limitCodes?.[limite.limitCode] || limite.limitCode;
    const cle = cleFenetre(limite.periodUnit);
    const fenetre = cle ? remplir(windows?.[cle], { n: limite.periodCount }) : '';
    const couleur = limite.atteinte ? 'bg-red-500' : limite.pourcentage >= 80 ? 'bg-amber-500' : 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink';

    return (
        <li className="py-2" data-testid="limite-ligne">
            <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-gray-800">
                    {nom}
                    {fenetre && <span className="ml-1 text-gray-500">({fenetre})</span>}
                </span>
                <span className={`tabular-nums font-medium ${limite.atteinte ? 'text-red-600' : 'text-gray-900'}`}>
                    {remplir(libelles.usage, { current: limite.current, max: limite.max })}
                </span>
            </div>
            <div className="h-1.5 mt-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div className={`h-full rounded-full ${couleur}`} style={{ width: `${limite.pourcentage}%` }} />
            </div>
        </li>
    );
}

/**
 * Les limites de la personne connectée.
 *
 * - sans `moduleCode` : la page « Limites », tous les modules sur une page ;
 * - avec `moduleCode` : un seul module (bandeau d'usage dans chaque module).
 *
 * Tout vient du serveur (GetLimitsOverview), qui sait quel palier s'applique :
 * un module couvert par un abonnement n'a plus aucune limite, et le dit.
 */
const LimitsOverview = ({ moduleCode = null, onSeeAll = null }) => {
    const { t, language } = useTranslation();
    const libelles = t.limitsPage || {};
    const [vue, setVue] = useState(null);
    const [chargement, setChargement] = useState(true);

    useEffect(() => {
        let annule = false;
        const uuid = localStorage.getItem('userUuid');
        setChargement(true);
        getLimitsOverview(uuid).then((v) => {
            if (!annule) { setVue(v); setChargement(false); }
        });
        return () => { annule = true; };
    }, []);

    if (chargement) {
        return <p className="text-sm text-gray-500">{t.common?.loading || '...'}</p>;
    }
    if (!vue) {
        return <p className="text-sm text-gray-500">{libelles.error}</p>;
    }

    const abonnements = lire(vue, 'subscriptions') || [];
    const payant = !!lire(vue, 'hasPaidPlan');
    const windows = t.limitReached || {};

    // --- Un seul module ---
    if (moduleCode) {
        const limites = limitesDuModule(vue, moduleCode);
        return (
            <div className="space-y-3">
                <EnTeteAbonnement vue={vue} language={language} libelles={libelles} />
                {limites.length === 0 ? (
                    <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid="module-sans-limite">
                        <InfiniteIcon className="w-4 h-4" />
                        {abonnements.length > 0 ? libelles.noLimitsPlan : libelles.noLimitsModule}
                    </p>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {limites.map((l) => <LigneLimite key={l.limitCode} limite={l} libelles={libelles} windows={windows} />)}
                    </ul>
                )}
                {onSeeAll && (
                    <button type="button" onClick={onSeeAll} className="text-sm text-cabaza-purple hover:underline">
                        {libelles.seeAll}
                    </button>
                )}
            </div>
        );
    }

    // --- Tous les modules, sur une page ---
    const modules = lire(vue, 'modules') || [];
    const annuaire = getModulesList();
    const nomModule = (code) => {
        const m = annuaire.find((x) => String(x.code || x.CODE).toUpperCase() === String(code).toUpperCase());
        return m?.name || m?.NAME || code;
    };
    const avecLimites = modules.filter((m) => (lire(m, 'limits') || []).length > 0);
    const sansLimites = modules.filter((m) => (lire(m, 'limits') || []).length === 0);

    return (
        <div className="space-y-6" data-testid="page-limites">
            <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
                <div className="flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-cabaza-purple" />
                    <h2 className="text-lg font-semibold text-gray-900">{libelles.title}</h2>
                </div>
                <p className="text-sm text-gray-600">{libelles.subtitle}</p>
                <EnTeteAbonnement vue={vue} language={language} libelles={libelles} />
                {payant && avecLimites.length === 0 && (
                    <p className="text-sm text-emerald-700 font-medium" data-testid="aucune-limite-abonnement">{libelles.noLimitsPlan}</p>
                )}
            </div>

            {avecLimites.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100">
                    {avecLimites.map((m) => (
                        <section key={lire(m, 'code')} className="p-5" data-testid={`limites-module-${lire(m, 'code')}`}>
                            <h3 className="text-sm font-semibold text-gray-900 mb-1">{nomModule(lire(m, 'code'))}</h3>
                            <ul className="divide-y divide-gray-50">
                                {limitesDuModule(vue, lire(m, 'code')).map((l) => (
                                    <LigneLimite key={l.limitCode} limite={l} libelles={libelles} windows={windows} />
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            )}

            {sansLimites.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm p-5">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">{libelles.unlimitedModules}</h3>
                    <p className="text-sm text-gray-600" data-testid="modules-sans-limite">
                        {sansLimites.map((m) => nomModule(lire(m, 'code'))).join(' · ')}
                    </p>
                </div>
            )}
        </div>
    );
};

export default LimitsOverview;
